import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/mahjong';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
            文章未找到
          </h1>
          <p className="text-foreground-600 mb-6">
            抱歉，您要查看的文章不存在或已被移除。
          </p>
          <Link
            to="/articles"
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回文章列表
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const contentHtml = article.content
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return '<br />';
      if (trimmed.startsWith('## ')) {
        return `<h2 class="font-heading text-xl font-bold text-foreground-900 mt-8 mb-4">${trimmed.slice(3)}</h2>`;
      }
      if (trimmed.startsWith('### ')) {
        return `<h3 class="font-heading text-lg font-semibold text-foreground-900 mt-6 mb-3">${trimmed.slice(4)}</h3>`;
      }
      if (trimmed.startsWith('- ')) {
        return `<li class="ml-4 mb-1 text-foreground-700">${trimmed.slice(2)}</li>`;
      }
      if (/^\d+\.\s/.test(trimmed)) {
        return `<li class="ml-4 mb-1 text-foreground-700">${trimmed.replace(/^\d+\.\s/, '')}</li>`;
      }
      return `<p class="text-foreground-700 leading-relaxed mb-3">${trimmed}</p>`;
    })
    .join('');

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          image: article.image,
          url: `https://tileblast.github.io/article/${article.slug}`,
          author: {
            '@type': 'Organization',
            name: '在线麻将牌',
          },
          publisher: {
            '@type': 'Organization',
            name: '在线麻将牌',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tileblast.github.io' },
            { '@type': 'ListItem', position: 2, name: '麻将攻略', item: 'https://tileblast.github.io/articles' },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        }}
      />

      <article className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '麻将攻略', path: '/articles' },
              { label: article.title },
            ]}
          />
        </div>

        {/* Header */}
        <header className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center gap-3 mb-4">
            <Link
              to={`/category/${article.category}`}
              className="bg-secondary-100 text-secondary-700 text-sm px-3 py-1 rounded-full hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              {article.categoryName}
            </Link>
            <span className="text-sm text-foreground-500 whitespace-nowrap">
              {article.date}
            </span>
            <span className="text-sm text-foreground-400">·</span>
            <span className="text-sm text-foreground-500 whitespace-nowrap flex items-center gap-1">
              <i className="ri-time-line text-xs"></i>
              {article.readTime}
            </span>
          </div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-foreground-600 text-base leading-relaxed">
            {article.excerpt}
          </p>
        </header>

        {/* Featured Image */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-auto max-h-[450px] object-cover rounded-xl"
            title={article.title}
          />
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 pb-14">
          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={ra.title}
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Back link */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 border-t border-background-200">
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回文章列表
          </Link>
        </div>
      </article>
    </>
  );
}